import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Menu, X } from 'lucide-react';

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/ops');
  const isBlueprint = location.pathname === '/blueprint';
  const isDark = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (isAdmin || isBlueprint) return null;

  const navLinks = [
    { href: '/homes-for-sale', label: 'Homes for Sale' },
    { href: '/neighbourhoods', label: 'Neighbourhoods' },
    { href: '/schools', label: 'Schools' },
    { href: '/relocation', label: 'Relocation' },
    { href: '/guides', label: 'Guides' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-sm'
          : isDark
            ? 'bg-transparent'
            : 'bg-white/60 backdrop-blur-md'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              scrolled || !isDark ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'
            }`}>B</div>
            <span className={`text-[15px] font-semibold tracking-tight transition-colors ${
              scrolled || !isDark ? 'text-slate-900' : 'text-white'
            }`}>
              Buy In Burlington
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3.5 py-2 text-[13px] font-medium rounded-full transition-all duration-200 ${
                  location.pathname === link.href
                    ? scrolled || !isDark ? 'text-slate-900 bg-slate-100' : 'text-white bg-white/15'
                    : scrolled || !isDark ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-50' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/blueprint" className="ml-3" data-testid="nav-start-plan">
              <Button className={`rounded-full px-5 h-9 text-[13px] font-medium shadow-lg transition-all hover:-translate-y-px ${
                scrolled || !isDark
                  ? 'bg-slate-900 text-white hover:bg-slate-800 shadow-slate-900/20'
                  : 'bg-white text-slate-900 hover:bg-slate-100 shadow-white/10'
              }`}>
                Start Blueprint
              </Button>
            </Link>
          </nav>

          <button
            className="md:hidden p-2 rounded-full"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? (
              <X className={`w-5 h-5 ${scrolled || !isDark ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-5 h-5 ${scrolled || !isDark ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 animate-fade-in" data-testid="mobile-nav">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl px-4 py-2.5 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/blueprint" onClick={() => setMobileOpen(false)} className="block mt-2">
                <Button className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 font-medium">
                  Start Blueprint
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
